import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { Subject } from 'rxjs';
import { DxDataGridComponent } from 'devextreme-angular';
import CustomStore from 'devextreme/data/custom_store';
import { SalonTypeService } from '../../../api/salon-type.service';
import { UserApi } from '../../../api';
import { FilterBuilderService } from '../../../service/filter-builder.service';
import { LoaderService } from '../../../core/loader/loader.service';
import {GridSetting, GridSettings, GridState, User} from '../../../shared';
import {ExtraFilteringOptions, FilterOperationModel, LoadOptions} from '../../../shared/models/shared.model';
import {SalonTypeMappingOption} from '../../../shared/models/salon-type.model';
import {MappingAction, MappingMode} from '../../../shared/enum/mapping-mode.enum';
import {take, takeUntil} from 'rxjs/operators';

@Component({
  selector: 'app-salon-type-mapping',
  templateUrl: 'salon-type-mapping.component.html'
})

export class SalonTypeMappingComponent implements OnInit, OnDestroy {
  @ViewChild('salonTypeMasterDataGrid') salonTypeMasterDataGrid: DxDataGridComponent;

  public salonTypeMasterDataSource = {};
  public salonTypeMasterSelectedRowMap = [];
  public currentUser: User;
  public extraFilteringOptions: ExtraFilteringOptions[] = [
	{ column: 'Active', value: false },
  ];
  public newHeaderDataSource;
  public mapHeaderDataSource;
  public saveHeaderDataSource;
  public omitHeaderDataSource;
  public dataProviderCodeHeaderDataSource;
  public inconsistencyHeaderDataSource;
  public createdByHeaderDataSource;
  public createdOnHeaderDataSource;
  public modifiedByHeaderDataSource;
  public modifiedOnHeaderDataSource;
  public reviewedByHeaderDataSource;
  public reviewedOnHeaderDataSource;
  public sourceSalonTypeHeaderDataSource;

  public mappingModeHeaderDataSource;
  public mappingModeNameHeaderDataSource;
  private _destroyed$ = new Subject();
  private salonTypeMappingDataLoading: boolean;
  private salonTypeMappingLoadingOptions: any;

  public isLoadSalonTypeMasterGridDataSource: boolean;
  private readonly masterGridName: string = 'salonTypeMasterDataGrid';
  private readonly pageName: string = 'salonTypeMapping';
  private currentSalonTypeMasterDataGridState: GridState;
  private salonTypeMasterDataGridSettings: GridSettings;
  private isSalonTypeMasterDataGridSettingLoading = true;
  private salonTypeMasterDataGridSetting: GridSetting = new GridSetting();

  constructor(
	private salonTypeService: SalonTypeService,
	private userService: UserApi,
	private filterBuilder: FilterBuilderService,
	private loaderService: LoaderService,
  ) {
  }

  ngOnInit() {
	this.loaderService.show();
	this.currentUser = this.userService.logonUser;
	if (!this.currentUser) {
		return;
	}
	this.salonTypeMasterDataGridSettings = {
		PageName: this.pageName,
		GridName: this.masterGridName,
		OwnedByUserId: this.currentUser.UserId
	};

	this.userService.getGridSettings(this.salonTypeMasterDataGridSettings)
		.pipe(take(1))
		.subscribe((settings) => {
		if (settings && settings.Settings) {
			if (typeof settings.Settings === 'string') {
			this.salonTypeMasterDataGridSetting = JSON.parse(settings.Settings);
			} else {
			this.salonTypeMasterDataGridSetting = settings.Settings;
			}
		} else {
			this.salonTypeMasterDataGridSettings.Settings = JSON.stringify({autosave: true});
			this.updateGridSettings(this.salonTypeMasterDataGridSettings);
		}
		this.loadSalonTypeMasterDataGridState();
		});

	this.loadColumnHeaderFilterData();
  }

  private loadSalonTypeMasterDataSource() {
	this.isSalonTypeMasterDataGridSettingLoading = false;
	this.salonTypeMasterDataSource = new CustomStore({
		key: '',
		load: (loadOptions: any) => {
		this.salonTypeMappingDataLoading = true;
		this.loaderService.show();
		const filter = this.formatFilterOperators(loadOptions.filter);
		const finalOptions: LoadOptions = loadOptions;
		finalOptions.filter = filter;
		finalOptions.extraParams = this.extraFilteringOptions;
		this.salonTypeMappingLoadingOptions = finalOptions;

		return this.salonTypeService.getSalonTypeMappingData(finalOptions)
			.toPromise()
			.then((data: any) => {
			this.salonTypeMappingDataLoading = false;
			this.loaderService.hide();
			return {
				data: data.data,
				totalCount: data.totalCount,
				summary: data.summary,
				groupCount: data.groupCount
			};
			})
			.catch(error => {
			this.salonTypeMappingDataLoading = false;
			this.loaderService.hide();
			throw new Error('Data Loading Error');
			});
		}
	});
	this.isLoadSalonTypeMasterGridDataSource = true;
  }

  private loadSalonTypeMasterDataGridState() {
	const salonTypeMasterDataGridState: GridState = {
		PageName: this.pageName,
		GridName: this.masterGridName,
		OwnedByUserId: this.currentUser.UserId
	};
	this.userService.getGridState(salonTypeMasterDataGridState)
		.pipe(takeUntil(this._destroyed$))
		.subscribe((state) => {
		if (state) {
			this.currentSalonTypeMasterDataGridState = state;
		} else {
			this.currentSalonTypeMasterDataGridState = {
			PageName: this.pageName,
			GridName: this.masterGridName,
			OwnedByUserId: this.currentUser.UserId,
			State: null
			};
		}
		this.loadSalonTypeMasterDataSource();
		});
  }

  public loadMasterState = () => {
	return this.currentSalonTypeMasterDataGridState.State ? JSON.parse(this.currentSalonTypeMasterDataGridState.State) : null;
  }

  public saveMasterState = (state) => {
	if (this.salonTypeMasterDataGridSetting) {
		let gridSetting: GridSetting;
		if (typeof this.salonTypeMasterDataGridSetting === 'string') {
		gridSetting = JSON.parse(this.salonTypeMasterDataGridSetting);
		} else {
		gridSetting = this.salonTypeMasterDataGridSetting;
		}
		if (gridSetting.autosave) {
		this.currentSalonTypeMasterDataGridState.State = JSON.stringify(state);
		this.updateGridState(this.currentSalonTypeMasterDataGridState);
		}
	}
  }

  private updateGridSettings(gridSettings: GridSettings) {
	gridSettings.SavedOn = new Date();
	this.userService.setGridSettings(gridSettings).pipe(take(1)).subscribe();
  }

  private updateGridState(gridState: GridState) {
	gridState.SavedOn = new Date();
	this.userService.setGridState(gridState).pipe(take(1)).subscribe();
  }

  private loadColumnHeaderFilterData() {
	this.newHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		const columnName = 'NewCount';
		loadOptions.column = columnName;
		loadOptions.valueColumn = '';
		loadOptions.filter = this.clearHeaderFilterParams(this.salonTypeMappingLoadingOptions, columnName);
		return this.getHeaderFiltersMapping(loadOptions);
		}
	});
	this.mapHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		const columnName = 'MapCount';
		loadOptions.column = columnName;
		loadOptions.valueColumn = '';
		loadOptions.filter = this.clearHeaderFilterParams(this.salonTypeMappingLoadingOptions, columnName);
		return this.getHeaderFiltersMapping(loadOptions);
		}
	});
	this.saveHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		const columnName = 'SaveCount';
		loadOptions.column = columnName;
		loadOptions.valueColumn = '';
		loadOptions.filter = this.clearHeaderFilterParams(this.salonTypeMappingLoadingOptions, columnName);
		return this.getHeaderFiltersMapping(loadOptions);
		}
	});
	this.omitHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		const columnName = 'OmitCount';
		loadOptions.column = columnName;
		loadOptions.valueColumn = '';
		loadOptions.filter = this.clearHeaderFilterParams(this.salonTypeMappingLoadingOptions, columnName);
		return this.getHeaderFiltersMapping(loadOptions);
		}
	});
	this.inconsistencyHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		const columnName = 'IsInconsistentMappingMessage';
		loadOptions.column = columnName;
		loadOptions.valueColumn = '';
		loadOptions.filter = this.clearHeaderFilterParams(this.salonTypeMappingLoadingOptions, columnName);
		return this.getHeaderFiltersMapping(loadOptions);
		}
	});
	this.dataProviderCodeHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		const columnName = 'DataProviderCode';
		loadOptions.column = columnName;
		loadOptions.valueColumn = '';
		loadOptions.filter = this.clearHeaderFilterParams(this.salonTypeMappingLoadingOptions, columnName);
		return this.getHeaderFiltersMapping(loadOptions);
		}
	});
	this.createdByHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		const columnName = 'CreatedByUserFullNameSortableOrPrincipalName';
		loadOptions.column = columnName;
		loadOptions.valueColumn = '';
		loadOptions.filter = this.clearHeaderFilterParams(this.salonTypeMappingLoadingOptions, columnName);
		return this.getHeaderFiltersMapping(loadOptions);
		}
	});
	this.createdOnHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		const columnName = 'CreatedOn';
		loadOptions.column = columnName;
		loadOptions.valueColumn = '';
		loadOptions.filter = this.clearHeaderFilterParams(this.salonTypeMappingLoadingOptions, columnName);
		return this.getHeaderFiltersMapping(loadOptions);
		}
	});
	this.modifiedByHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		const columnName = 'ModifiedByUserFullNameSortableOrPrincipalName';
		loadOptions.column = columnName;
		loadOptions.valueColumn = '';
		loadOptions.filter = this.clearHeaderFilterParams(this.salonTypeMappingLoadingOptions, columnName);
		return this.getHeaderFiltersMapping(loadOptions);
		}
	});
	this.modifiedOnHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		const columnName = 'ModifiedOn';
		loadOptions.column = columnName;
		loadOptions.valueColumn = '';
		loadOptions.filter = this.clearHeaderFilterParams(this.salonTypeMappingLoadingOptions, columnName);
		return this.getHeaderFiltersMapping(loadOptions);
		}
	});
	this.reviewedByHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		const columnName = 'ReviewedByUserFullNameSortableOrPrincipalName';
		loadOptions.column = columnName;
		loadOptions.valueColumn = '';
		loadOptions.filter = this.clearHeaderFilterParams(this.salonTypeMappingLoadingOptions, columnName);
		return this.getHeaderFiltersMapping(loadOptions);
		}
	});
	this.reviewedOnHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		const columnName = 'ReviewedOn';
		loadOptions.column = columnName;
		loadOptions.valueColumn = '';
		loadOptions.filter = this.clearHeaderFilterParams(this.salonTypeMappingLoadingOptions, columnName);
		return this.getHeaderFiltersMapping(loadOptions);
		}
	});
	this.sourceSalonTypeHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		const columnName = 'SourceSalonType';
		loadOptions.column = columnName;
		loadOptions.valueColumn = '';
		loadOptions.filter = this.clearHeaderFilterParams(this.salonTypeMappingLoadingOptions, columnName);
		return this.getHeaderFiltersMapping(loadOptions);
		}
	});
	this.mappingModeNameHeaderDataSource = [
		{
		text: 'New',
		value: 0
		},
		{
		text: 'Map',
		value: 1
		},
		{
		text: 'Save',
		value: 2
		},
		{
		text: 'Omit',
		value: 2
		}
	];
  }

  private clearHeaderFilterParams(loadingFilter: FilterOperationModel[], columnName: string) {
	let res = null;
	if (loadingFilter && Array.isArray(loadingFilter)) {
		res = loadingFilter.filter(item => item.column !== columnName);
		return res;
	} else if (loadingFilter) {
		const filterList = loadingFilter['filterList'];
		if (filterList) {
		res = filterList.filter(item => item.column !== columnName);
		return res;
		}
	}
	return res;
  }

  getHeaderFiltersMapping(loadingOption) {
	return this.salonTypeService.getHeaderFiltersSalonTypeMapping(loadingOption)
		.toPromise()
		.catch(error => { throw new Error('Data Loading Error'); });
  }

  selectionMasterDataChangedHandler() {
	if (this.salonTypeMasterDataGrid.instance.getSelectedRowsData()[0]) {
		this.salonTypeMasterSelectedRowMap = this.salonTypeMasterDataGrid.instance.getSelectedRowsData()[0];
	}
  }

  /**
   * Context menu for SalonType mapping data grid
   * @param event
   */
  salonTypeMasterDataGridContextMenu(e: any) {
	if (e.target == 'content') {
		// e.items can be undefined
		if (!e.items) { e.items = []; }

		// Add a custom menu item
		e.items.push(
		{
			text: 'Refresh',
			icon: 'refresh',
			onItemClick: () => {
			this.refreshSalonTypeMasterDataGrid();
			}
		},
		{
			text: 'Grid Settings',
			items: [
			{
				text: this.salonTypeMasterDataGridSetting.autosave ? 'Disable autosave' : 'Enable autosave',
				onItemClick: () => {
				this.salonTypeMasterDataGridSetting.autosave = !this.salonTypeMasterDataGridSetting.autosave;
				this.salonTypeMasterDataGridSettings.Settings = JSON.stringify(this.salonTypeMasterDataGridSetting);
				this.updateGridSettings(this.salonTypeMasterDataGridSettings);
				}
			}
			]
		}
		);
	}
  }

  formatFilterOperators(rawFilter: any[]) {
	if (!rawFilter) {
		return null;
	}
	const filterData = this.filterBuilder.handleNotFilterFormatting(rawFilter);

	return this.filterBuilder.filterFormatting(filterData);
  }

  private refreshSalonTypeMasterDataGrid() {
	this.salonTypeMasterDataGrid.instance.refresh();
  }

  deactivate(salonTypeMappingId: any) {
	const mappingData: SalonTypeMappingOption = {};
	mappingData.ModifiedByUserId = this.currentUser.UserId;
	mappingData.SalonTypeMappingId = salonTypeMappingId;
	mappingData.ActionType = MappingAction.Deactivate;
	this.loaderService.show();
	this.salonTypeService.mappingAction(mappingData)
		.pipe(takeUntil(this._destroyed$))
		.subscribe((res) => {
		this.refreshSalonTypeMasterDataGrid();
		});
  }

  reactivate(salonTypeMappingId: any) {
	const mappingData: SalonTypeMappingOption = {};
	mappingData.ModifiedByUserId = this.currentUser.UserId;
	mappingData.SalonTypeMappingId = salonTypeMappingId;
	mappingData.ActionType = MappingAction.Reactivate;
	this.loaderService.show();
	this.salonTypeService.mappingAction(mappingData)
		.pipe(takeUntil(this._destroyed$))
		.subscribe((res) => {
		this.refreshSalonTypeMasterDataGrid();
		});
  }

  omit(salonTypeMappingId: any) {
	const mappingData: SalonTypeMappingOption = {};
	mappingData.MappingModeId = MappingMode.Omitted;
	mappingData.ModifiedByUserId = this.currentUser.UserId;
	mappingData.SalonTypeMappingId = salonTypeMappingId;
	mappingData.ActionType = MappingAction.Omit;
	this.loaderService.show();
	this.salonTypeService.mappingAction(mappingData)
		.pipe(takeUntil(this._destroyed$))
		.subscribe((res) => {
		this.refreshSalonTypeMasterDataGrid();
		});
  }

  save(salonTypeMappingId: any) {
	const mappingData: SalonTypeMappingOption = {};
	mappingData.MappingModeId = MappingMode.Saved;
	mappingData.ModifiedByUserId = this.currentUser.UserId;
	mappingData.SalonTypeMappingId = salonTypeMappingId;
	mappingData.ActionType = MappingAction.Save;
	this.loaderService.show();

	this.salonTypeService.mappingAction(mappingData)
		.pipe(takeUntil(this._destroyed$))
		.subscribe((res) => {
		this.refreshSalonTypeMasterDataGrid();
		});
  }

  map(salonTypeMappingId: any) {
	const mappingData: SalonTypeMappingOption = {};
	mappingData.MappingModeId = MappingMode.Mapped;
	mappingData.ModifiedByUserId = this.currentUser.UserId;
	mappingData.SalonTypeMappingId = salonTypeMappingId;
	mappingData.ActionType = MappingAction.Omit;
	this.loaderService.show();

	this.salonTypeService.mappingAction(mappingData)
		.pipe(takeUntil(this._destroyed$))
		.subscribe((res) => {
		this.refreshSalonTypeMasterDataGrid();
		});
  }

  accept(salonTypeMappingId) {
	const mappingData: SalonTypeMappingOption = {};
	mappingData.ModifiedByUserId = this.currentUser.UserId;
	mappingData.SalonTypeMappingId = salonTypeMappingId;
	mappingData.ActionType = MappingAction.Accept;
	this.loaderService.show();
	this.salonTypeService.mappingAction(mappingData)
		.pipe(takeUntil(this._destroyed$))
		.subscribe((res) => {
		this.refreshSalonTypeMasterDataGrid();
		});
  }

  review(salonTypeMappingId) {
	const mappingData: SalonTypeMappingOption = {};
	mappingData.ModifiedByUserId = this.currentUser.UserId;
	mappingData.SalonTypeMappingId = salonTypeMappingId;
	mappingData.ActionType = MappingAction.Review;
	this.loaderService.show();
	this.salonTypeService.mappingAction(mappingData)
		.pipe(takeUntil(this._destroyed$))
		.subscribe((res) => {
		this.refreshSalonTypeMasterDataGrid();
		});
  }

  private loaderHandler() {
	if (
		!this.isSalonTypeMasterDataGridSettingLoading &&
		!this.salonTypeMappingDataLoading) {
		this.loaderService.hide();
	}
  }

  ngOnDestroy() {
	this.loaderService.hide();
	this._destroyed$.next(null);
	this._destroyed$.complete();
  }
}
